import React, { Fragment, createElement } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';

import { useRouter } from 'next/router';
import getCanonicalUrl from './getCanonicalUrl';
import { getBasePath } from './navigation';
import SLIHyphenMap from './SLIHyphenMap.json';
import metaOverrides, { getOverrideMeta } from './metaOverrides';
import formatString from './formatString';

const DEFAULTS = {
  description: 'Your favorite children’s luxury brands and independent boutiques, all in one magical place. Shop Maisonette for the best in kids clothing, furniture, decor, or toys.',
  image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette.jpg`,
  title: 'Maisonette - Online Shop For Children\'s Clothes, Furniture, Decor, Gifts | Maisonette'
};

export const getMetaImage = (props) => {
  if (props?.page?.socialImage?.uri) {
    const img = props.page.socialImage;

    if (img.types?.length > 0) {
      const type = img.types[img.types.length - 1];

      let src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName.split('.')[0]}-large.${type}`;
      if (props?.preview) {
        src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName}`;
      } else if (img.raw || !img.uri) {
        src = `${process.env.NEXT_PUBLIC_ASSET_HOST}/${img.uri}${img.imageName}`;
      }

      return src;
    }
  }

  return DEFAULTS.image;
};

export const meta = {
  title: DEFAULTS.title,
  description: DEFAULTS.description,
  og: {
    description: DEFAULTS.description,
    image: DEFAULTS.image,
    title: DEFAULTS.title,
    type: 'website',
    url: process.env.NEXT_PUBLIC_CLIENT_HOST
  },
  twitter: {
    card: 'summary_large_image',
    description: DEFAULTS.description,
    image: DEFAULTS.image,
    title: DEFAULTS.title
  }
};

export const generateMeta = (o = {}) => {
  const options = { ...meta, key: 'default', ...o };
  const children = [];

  /* HTML Meta Tags */
  children.push(createElement('title', { key: 'title' }, options.title));
  children.push(createElement('meta', { key: 'description', name: 'description', content: options.description }));

  /* Facebook Meta Tags */
  Object.entries(options.og).forEach(([key, value]) => {
    if (value != null) children.push(createElement('meta', { key: `og-${key}`, property: `og:${key}`, content: value }));
    else children.push(createElement('meta', { key: `og-${key}`, property: `og:${key}`, content: meta.og[key] }));
  });

  /* Twitter Meta Tags */
  Object.entries(options.twitter).forEach(([key, value]) => {
    if (value != null) children.push(createElement('meta', { key: `twitter-${key}`, name: `twitter:${key}`, content: value }));
    else children.push(createElement('meta', { key: `twitter-${key}`, name: `twitter:${key}`, content: meta.twitter[key] }));
  });

  return createElement(Fragment, {}, [...children]);
};

export const getDescription = (key, values, mutator) => {
  const MAX_LENGTH = 158;
  const BreakException = {};

  if (values) {
    try {
      values.forEach((c, i) => {
        if (i === 0) mutator.push(`including ${c[key]}`);
        else if (i === values.length - 1) mutator.push(`and ${c[key]}`);
        else {
          const temp = mutator.slice(0);
          temp.push(c[key]);
          temp.push(` and ${values[i + 1][key]}`);

          if (`${temp.join(', ')}.`.length >= MAX_LENGTH) {
            mutator.push(`and ${c[key]}`);
            throw BreakException;
          } else {
            mutator.push(c[key]);
          }
        }
      });
    } catch (e) {
      if (e !== BreakException) throw e;
    }
  }

  return `${mutator.join(', ')}.`;
};

export const getPLPDescription = (activeCategories = [], facets = false, products = false) => {
  const description = [activeCategories.map((c) => {
    if (typeof c === 'string') return c;
    return c.name;
  }).join(' ')];

  if (!facets || !products) return DEFAULTS.description;

  if (activeCategories.length <= 0 || activeCategories.length === 3) {
    return getDescription('title', products, description);
  }

  const categories = facets.filter(
    (f) => /^cat[1-9]+$/.test(f.id) && +f.id.split('cat')[1] > activeCategories.length
  ).reduce((acc, curr) => [...acc, curr.values], [])[0];

  return getDescription('name', categories, description);
};

const getBreadcrumbCats = (props) => {
  let crumbs;

  if (props?.product) {
    crumbs = props.product.breadcrumb_taxons;
  }

  if (props?.headers?.subHeaders) {
    // a plp will have subheaders that are not links on the page
    // use these for breadcrumbs
    crumbs = props.headers.subHeaders;

    if (props.trend || props.lpc || props.edit) {
      // a trend/lpc/edit plp will have subheaders we can use (set above), but we need to add the
      // trend/lpc/edit to position 1 in the breadcrumbs
      const name = props.trendName || props.lpc || props.editName;
      const baseCrumb = {
        name: formatString('capitalize', name)
      };

      crumbs = [baseCrumb, ...crumbs];
    }
  }

  return crumbs;
};

const hyphenateCrumbId = (crumb) => {
  const parts = crumb?.id?.split?.('_');
  const hyphenatedParts = parts?.map?.((part) => SLIHyphenMap[part] ?? part);
  const id = hyphenatedParts?.join('_') ?? crumb.id;
  return {
    ...crumb,
    id
  };
};

const createCrumbItem = (crumb, index, props) => {
  const cats = crumb.url?.split('/category/')[1];
  let path = getBasePath(props);
  if (crumb?.url) path = `/shop/${cats}`;
  if (!crumb?.url && crumb?.id) path = `${path}/${crumb.id.split('_').join('/')}`;

  const item = `${process.env.NEXT_PUBLIC_CLIENT_HOST}${path}`;
  return ({
    '@type': 'ListItem',
    position: `${index + 1}`,
    name: `${crumb.name}`,
    item
  });
};

const generateBreadcrumbItems = (crumbs, props) => {
  const breadcrumbItems = crumbs?.reduce?.((acc, curr, index) => {
    const crumb = hyphenateCrumbId(curr);
    const item = createCrumbItem(crumb, index, props);
    acc.push(item);
    return acc;
  }, []);

  return breadcrumbItems?.length > 0 && JSON.stringify(breadcrumbItems || []);
};

export const generateBreadcrumbSeoSchema = (props) => {
  const crumbs = getBreadcrumbCats(props);
  const items = generateBreadcrumbItems(crumbs, props);

  return (
    items && (
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
            {
              "@context": "https://schema.org/",
              "@type": "BreadcrumbList",
              "itemListElement": ${items}
            }
          `
        }}
      />
    )
  );
};

const getLPCMetaTitle = (lp) => (lp?.title ? lp?.title.split('<title>')[1].split('</title>')[0] : '');

const PLPMeta = (props) => {
  const router = useRouter();
  const getMetaData = () => {
    const activeCategories = props?.algoliaCategories ? props.algoliaCategories
      : Object.values(props.categories?.active ?? []);

    if (props.trendName) activeCategories.unshift(formatString('capitalize', props.trendName));
    const metaData = {
      shop: () => ({
        title: `Shop ${props.headers?.header?.name ?? ''}${props.headers?.subHeaders?.map?.((h) => `, ${h?.name}`).join('') ?? ''} | Maisonette`,
        description: getPLPDescription(activeCategories, props?.facets, props?.results),
        ...metaOverrides(activeCategories, 'shop', props)
      }),
      trend: () => {
        const headers = props.headers
          ? ` - ${props.headers?.header?.name}${props.headers?.subHeaders?.map?.((h) => `, ${h?.name}`).join('') ?? ''}`
          : '';

        return {
          title: `Shop ${formatString('capitalize', props.trendName || props.trend)}${headers} | Maisonette`,
          description: getPLPDescription(activeCategories, props?.facets, props?.results),
          ...metaOverrides(activeCategories, 'trend', props)
        };
      },
      edit: () => ({
        title: `${props.editName || props.edit} | Maisonette`,
        description: getPLPDescription(activeCategories, props?.facets, props?.results),
        ...metaOverrides(activeCategories, 'edit', props)
      }),
      lpc: () => ({
        title: `${getLPCMetaTitle(props.lp)} | Maisonette`,
        description: getPLPDescription(activeCategories, props?.facets, props?.results),
        ...metaOverrides(activeCategories, 'lpc', props)
      })
    };

    if (props.noResults) {
      let pageType;
      let noResultsMeta;

      try {
        pageType = Object.keys(props).find((k) => ['brand', 'lpc', 'edit', 'trend'].includes(k) && props[k] && k);
        noResultsMeta = pageType
          ? metaOverrides(activeCategories, pageType, props)
          : metaData.shop();
      } catch (error) { /* noop */ }

      if (props.searchTerm !== '*') {
        const title = `No results for '${props.searchTerm}'${props.correctedSearchTerm ? ` corrected from '${props.correctedSearchTerm}'` : ''} | Maisonette`;

        return generateMeta({
          key: 'no-results',
          title,
          facebook: { title },
          twitter: { title }
        });
      }

      const noResultsTitle = props.categories?.active
        ? `No results for ${props.categories?.active?.cat1?.name} | Maisonette`
        : 'No results | Maisonette';

      const title = noResultsMeta ? noResultsMeta.title : noResultsTitle;
      const description = noResultsMeta
        ? noResultsMeta.description
        : getPLPDescription(activeCategories, props?.facets, props?.results);

      return noResultsMeta ? generateMeta({
        key: pageType,
        title: props.lp ? `${getLPCMetaTitle(props.lp)} | Maisonette` : noResultsMeta.title,
        description,
        og: {
          title: props.lp ? `${getLPCMetaTitle(props.lp)} | Maisonette` : noResultsMeta.title,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description
        },
        twitter: {
          title: props.lp ? `${getLPCMetaTitle(props.lp)} | Maisonette` : noResultsMeta.title,
          image: props.results?.[0]?.image,
          description
        }
      }) : generateMeta({
        key: 'no-results', title, facebook: { title }, twitter: { title }
      });
    }

    if (props.searchTerm !== '*' || props.searchState?.query) {
      const searchTerm = props.searchState?.query || props.searchTerm;
      return generateMeta({
        key: 'search-results',
        title: `Search results for ${searchTerm} | Maisonette`,
        facebook: { title: `Search results for ${searchTerm} | Maisonette` },
        twitter: { title: `Search results for ${searchTerm} | Maisonette` }
      });
    }

    if (props.lpc) {
      const lpcMetaData = metaData.lpc();
      return generateMeta({
        key: 'lpc',
        title: lpcMetaData.title,
        description: lpcMetaData.description,
        og: {
          title: lpcMetaData.title,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: lpcMetaData.description
        },
        twitter: {
          title: lpcMetaData.title,
          image: props.results?.[0]?.image,
          description: lpcMetaData.description
        }
      });
    }

    if (props.brand) {
      const brandMetaData = getOverrideMeta([], 'brand', props);

      return generateMeta({
        key: 'brands',
        title: brandMetaData.title,
        description: brandMetaData.description,
        og: {
          description: brandMetaData.description,
          image: props.brand.icon !== `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default_taxon.jpg` ? props.brand.icon : props.results?.[0]?.image,
          title: brandMetaData.title,
          url: global.window?.location
        },
        twitter: {
          description: brandMetaData.description,
          image: props.brand.icon !== `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default_taxon.jpg` ? props.brand.icon : props.results?.[0]?.image,
          title: brandMetaData.title
        }
      });
    }

    if (props.edit) {
      const editMetaData = metaData.edit();
      return generateMeta({
        key: 'edit',
        title: editMetaData.title,
        description: editMetaData.description,
        og: {
          title: editMetaData.title,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: editMetaData.description
        },
        twitter: {
          title: editMetaData.title,
          image: props.results?.[0]?.image,
          description: editMetaData.description
        }
      });
    }

    if (props.trend) {
      const trendMetaData = metaData.trend();

      return generateMeta({
        key: 'trend',
        title: trendMetaData.title,
        description: trendMetaData.description,
        og: {
          title: trendMetaData.title,
          image: props.results?.[0]?.image,
          url: global.window?.location,
          description: trendMetaData.description
        },
        twitter: {
          title: trendMetaData.title,
          image: props.results?.[0]?.image,
          description: trendMetaData.description
        }
      });
    }

    const shopMeta = metaData.shop();

    return generateMeta({
      key: 'shop',
      title: shopMeta.title,
      description: shopMeta.description,
      og: {
        title: shopMeta.title,
        image: props.results?.[0]?.image,
        url: global.window?.location,
        description: shopMeta.description
      },
      twitter: {
        title: shopMeta.title,
        image: props.results?.[0]?.image,
        description: shopMeta.description
      }
    });
  };
  return (
    <Head>
      {getMetaData(props)}
      {generateBreadcrumbSeoSchema(props)}
      {router && <link key="canonical" rel="canonical" href={getCanonicalUrl(router)} />}
    </Head>
  );
};

PLPMeta.defaultProps = {
  brand: null,
  categories: null,
  correctedSearchTerm: null,
  edit: null,
  editName: null,
  headers: null,
  lp: null,
  lpc: null,
  noResults: false,
  results: null,
  searchTerm: '*',
  trend: null,
  trendName: null,
  searchState: null,
  algoliaCategories: null
};

PLPMeta.propTypes = {
  brand: PropTypes.object,
  categories: PropTypes.object,
  correctedSearchTerm: PropTypes.string,
  edit: PropTypes.string,
  editName: PropTypes.string,
  headers: PropTypes.object,
  lp: PropTypes.object,
  lpc: PropTypes.string,
  noResults: PropTypes.bool,
  results: PropTypes.array,
  searchTerm: PropTypes.string,
  searchState: PropTypes.object,
  trend: PropTypes.string,
  trendName: PropTypes.string,
  algoliaCategories: PropTypes.array
};

export default PLPMeta;
